import { useEffect, useState } from 'react';

export interface PortalWrapperProps {
	tagName?: string;
	className?: string;
	id?: string;
}

export function usePortalAfter(
	selector: string | undefined,
	wrapper?: PortalWrapperProps
): HTMLElement | null {
	const [container, setContainer] = useState<HTMLElement | null>(null);

	const tagName = wrapper?.tagName ?? 'div';
	const className = wrapper?.className;
	const id = wrapper?.id;

	useEffect(() => {
		if (!selector) return;

		let el: HTMLElement | null = null;

		function attach(target: Element) {
			if (!target.parentNode) return;
			el = document.createElement(tagName);
			if (className) {
				el.className = className;
			} else {
				el.style.display = 'contents';
			}
			if (id) el.id = id;
			target.after(el);
			setContainer(el);
		}

		const existing = document.querySelector(selector);
		if (existing) {
			attach(existing);
			return () => {
				el?.remove();
				setContainer(null);
			};
		}

		// Element not yet in DOM — wait for it via MutationObserver.
		const observer = new MutationObserver(() => {
			const target = document.querySelector(selector);
			if (target) {
				observer.disconnect();
				attach(target);
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
			el?.remove();
			setContainer(null);
		};
	}, [selector, tagName, className, id]);

	return container;
}
